import React, { Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { useSettings } from "@/lib/settings-context";
import { ThemeProvider } from "@/lib/theme-context";
import { SettingsProvider } from "@/lib/settings-context";
import { ConfirmProvider } from "@/lib/confirm-context";
import { ScopeFilterProvider } from "@/lib/scope-filter-context";
import { EntitlementsProvider, useEntitlements } from "@/lib/use-entitlements";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/layout/AppShell";
import AppBootGate from "@/components/layout/AppBootGate";
import ScriptsInjector from "@/components/tracking/ScriptsInjector";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import SsoCallback from "@/pages/SsoCallback";
import AcceptInvite from "@/pages/AcceptInvite";
import AcceptWorkspaceInvite from "@/pages/AcceptWorkspaceInvite";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Workspace from "@/pages/Workspace";
import Dashboards from "@/pages/Dashboards";
import GalleryPreview from "@/pages/GalleryPreview";
import Gallery from "@/pages/Gallery";
import SenseAI from "@/pages/SenseAI";
import Users from "@/pages/Users";
import Governance from "@/pages/Governance";
const UserAccessReview = React.lazy(() => import("@/pages/governance/UserAccessReview"));
const RbacChangeHistory = React.lazy(() => import("@/pages/governance/RbacChangeHistory"));
const ActivityReport = React.lazy(() => import("@/pages/governance/ActivityReport"));
const PrivilegedAccessReview = React.lazy(() => import("@/pages/governance/PrivilegedAccessReview"));
const UsageSummary = React.lazy(() => import("@/pages/governance/UsageSummary"));
const ScheduledJobs = React.lazy(() => import("@/pages/governance/ScheduledJobs"));
const CostSummary = React.lazy(() => import("@/pages/governance/CostSummary"));
const ConnectorAddonSpend = React.lazy(() => import("@/pages/governance/ConnectorAddonSpend"));

// iter-119aa (G12) — lazy-load heavy, less-frequently-used pages so the
// initial bundle ships <Login/Home/Dashboards/SenseAI/Users> and code-
// splits the rest. Cuts TTI on slow connections; user-perceived effect
// kicks in on the FIRST navigation to one of these tabs, which is
// almost always a click (not a refresh).
const Data = React.lazy(() => import("@/pages/Data"));
const AuditActivityLog = React.lazy(() => import("@/pages/AuditActivityLog"));
const Settings = React.lazy(() => import("@/pages/Settings"));
const Branding = React.lazy(() => import("@/pages/Branding")); // iter-121.98 — new Branding page
const Tracking = React.lazy(() => import("@/pages/Tracking")); // Settings → Tracking (analytics framework)
const SetupChecklistPage = React.lazy(() => import("@/pages/SetupChecklistPage"));
const Billing = React.lazy(() => import("@/pages/Billing"));
const PlatformConnectors = React.lazy(() => import("@/pages/PlatformConnectors"));
const PlatformTemplates = React.lazy(() => import("@/pages/PlatformTemplates"));
const BuilderStudio = React.lazy(() => import("@/pages/BuilderStudio"));
const YamlConnectors = React.lazy(() => import("@/pages/YamlConnectors"));
const AppStudio = React.lazy(() => import("@/pages/AppStudio"));
const MyApps = React.lazy(() => import("@/pages/MyApps"));
const Marketplace = React.lazy(() => import("@/pages/Marketplace"));
const MyAppDetail = React.lazy(() => import("@/pages/MyAppDetail"));
const UsageReports = React.lazy(() => import("@/pages/UsageReports"));
const RolesPage = React.lazy(() => import("@/pages/Roles"));
const Organizations = React.lazy(() => import("@/pages/Organizations"));
const DataModels = React.lazy(() => import("@/pages/DataModels"));
const DataControl = React.lazy(() => import("@/pages/DataControl"));
const FeatureCatalog = React.lazy(() => import("@/pages/FeatureCatalog"));
const BusinessRules = React.lazy(() => import("@/pages/BusinessRules"));
const ReleaseNotes = React.lazy(() => import("@/pages/ReleaseNotes"));
const BillingGuide = React.lazy(() => import("@/pages/BillingGuide"));
const ScopeMatrix = React.lazy(() => import("@/pages/ScopeMatrix"));
const Playbooks = React.lazy(() => import("@/pages/Playbooks"));
const Schedules = React.lazy(() => import("@/pages/Schedules"));
const Documentation = React.lazy(() => import("@/pages/Documentation"));
const HelpHome = React.lazy(() => import("@/pages/HelpHome"));
const HowToGuides = React.lazy(() => import("@/pages/HowToGuides"));
const Glossary = React.lazy(() => import("@/pages/Glossary"));
const FaqHelp = React.lazy(() => import("@/pages/FaqHelp"));

function PageFallback() {
    return (
        <div className="min-h-[40vh] flex items-center justify-center text-sm text-muted-foreground"
             data-testid="page-suspense-fallback">
            Loading page…
        </div>
    );
}

// Legacy "/app-catalog" bookmarks → new My Apps / Marketplace pages.
function LegacyAppCatalogRedirect() {
    const loc = useLocation();
    const tab = new URLSearchParams(loc.search).get("tab");
    return <Navigate to={tab === "browse" ? "/marketplace" : "/my-apps"} replace />;
}
function LegacyMyAppDetailRedirect() {
    const { iid } = useParams();
    return <Navigate to={`/my-apps/${iid}`} replace />;
}

function Protected({ children, roles, module }) {
    const { user, loading } = useAuth();
    const { canAccess } = useSettings();
    const { entitled } = useEntitlements();
    const loc = useLocation();
    if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
    if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
    // Plan → RBAC. Route guard: HIDE plan-gated modules on direct URL access,
    // composed on top of the RBAC matrix (never replaces it).
    const PLAN_MODULE_FEATURE = { my_apps: "app_catalog", marketplace: "app_catalog", branding_page: "custom_branding", app_studio: "app_studio" };
    if (module && PLAN_MODULE_FEATURE[module] && user.role !== "super_admin"
        && !entitled(PLAN_MODULE_FEATURE[module])) {
        return <Navigate to="/" replace />;
    }
    if (module && !canAccess(module)) return <Navigate to="/" replace />;
    if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
    return children;
}

export default function App() {
    return (
        <AuthProvider>
            <ThemeProvider>
                <SettingsProvider>
                <EntitlementsProvider>
                <AppBootGate>
                <ConfirmProvider>
                <BrowserRouter>
                    <ScopeFilterProvider>
                    <ScriptsInjector />
                    <Suspense fallback={<PageFallback />}>
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/sso/callback" element={<SsoCallback />} />
                        <Route path="/accept-invite" element={<AcceptInvite />} />
                        <Route path="/accept-workspace-invite" element={<AcceptWorkspaceInvite />} />
                        <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
                        <Route path="/" element={<Protected><AppShell /></Protected>}>
                            <Route index element={<Home />} />
                            <Route path="workspace" element={<Workspace />} />
                            <Route path="data" element={<Data />} />
                            <Route path="data-control" element={<Protected roles={["super_admin", "org_admin"]} module="data_control"><DataControl /></Protected>} />
                            <Route path="dashboards" element={<Dashboards />} />
                            <Route path="dashboards/:id" element={<Dashboards />} />
                            <Route path="playbooks" element={<Protected module="playbooks"><Playbooks /></Protected>} />
                            <Route path="dashboards/preview/:id" element={<GalleryPreview />} />
                            <Route path="gallery/preview/:id" element={<GalleryPreview />} />
                            <Route path="gallery" element={<Gallery />} />
                            <Route path="sense-ai" element={<SenseAI />} />
                            <Route path="users" element={<Users />} />
                            <Route path="organizations" element={<Organizations />} />
                            <Route path="roles" element={<Protected roles={["super_admin"]} module="roles"><RolesPage /></Protected>} />
                            <Route path="activity" element={<AuditActivityLog />} />
                            <Route path="audit-log" element={<AuditActivityLog />} />
                            <Route path="audit-activity" element={<AuditActivityLog />} />
                            <Route path="governance" element={<Protected module="governance"><Governance /></Protected>} />
                            <Route path="governance/user-access-review" element={<Protected module="governance"><UserAccessReview /></Protected>} />
                            <Route path="governance/privileged-access" element={<Protected module="governance"><PrivilegedAccessReview /></Protected>} />
                            <Route path="governance/rbac-change-history" element={<Protected module="governance"><RbacChangeHistory /></Protected>} />
                            <Route path="governance/usage-summary" element={<Protected module="governance"><UsageSummary /></Protected>} />
                            <Route path="governance/scheduled-jobs" element={<Protected module="governance"><ScheduledJobs /></Protected>} />
                            <Route path="governance/cost-summary" element={<Protected module="governance"><CostSummary /></Protected>} />
                            <Route path="governance/connector-addon-spend" element={<Protected module="governance" roles={["super_admin"]}><ConnectorAddonSpend /></Protected>} />
                            <Route path="governance/r/:kind" element={<Protected module="governance"><ActivityReport /></Protected>} />
                            <Route path="settings" element={<Protected roles={["super_admin", "org_admin", "entity_admin"]}><Settings /></Protected>} />
                            <Route path="settings/branding" element={<Protected module="branding_page"><Branding /></Protected>} />
                            <Route path="settings/tracking" element={<Protected module="tracking_page"><Tracking /></Protected>} />
                            <Route path="settings/setup-checklist" element={<Protected module="setup_checklist_page"><SetupChecklistPage /></Protected>} />
                            <Route path="subscription" element={<Navigate to="/billing" replace />} />
                            <Route path="billing" element={<Protected module="billing"><Billing /></Protected>} />
                            <Route path="platform/connectors" element={<Protected module="platform_connectors"><PlatformConnectors /></Protected>} />
                            <Route path="yaml-connectors" element={<Protected module="yaml_connectors"><YamlConnectors /></Protected>} />
                            <Route path="app-studio" element={<Protected module="app_studio" roles={["super_admin"]}><AppStudio /></Protected>} />
                            <Route path="builder-studio" element={<Protected module="builder_studio" roles={["super_admin"]}><BuilderStudio /></Protected>} />
                            <Route path="my-apps" element={<Protected module="my_apps"><MyApps /></Protected>} />
                            <Route path="my-apps/:iid" element={<Protected module="my_apps"><MyAppDetail /></Protected>} />
                            <Route path="marketplace" element={<Protected module="marketplace"><Marketplace /></Protected>} />
                            <Route path="app-catalog" element={<LegacyAppCatalogRedirect />} />
                            <Route path="app-catalog/my-apps/:iid" element={<LegacyMyAppDetailRedirect />} />
                            <Route path="platform/templates" element={<Protected module="platform_templates"><PlatformTemplates /></Protected>} />
                            <Route path="usage-reports" element={<Protected module="usage_reports"><UsageReports /></Protected>} />
                            <Route path="schedules" element={<Schedules />} />
                            <Route path="data-models" element={<Protected roles={["super_admin"]}><DataModels /></Protected>} />
                            <Route path="feature-catalog" element={<Protected roles={["super_admin"]} module="feature_catalog"><FeatureCatalog /></Protected>} />
                            <Route path="business-rules" element={<Protected roles={["super_admin"]} module="business_rules"><BusinessRules /></Protected>} />
                            <Route path="release-notes" element={<Protected roles={["super_admin"]} module="release_notes"><ReleaseNotes /></Protected>} />
                            <Route path="scope-matrix" element={<ScopeMatrix />} />
                            <Route path="billing-guide" element={<Protected module="billing_guide"><BillingGuide /></Protected>} />
                            <Route path="documentation" element={<Protected roles={["super_admin"]} module="documentation"><Documentation /></Protected>} />
                            <Route path="help" element={<Protected module="help_home"><HelpHome /></Protected>} />
                            <Route path="help/guides" element={<Protected module="how_to_guides"><HowToGuides /></Protected>} />
                            <Route path="help/glossary" element={<Protected module="glossary"><Glossary /></Protected>} />
                            <Route path="help/faq" element={<Protected module="faq_help"><FaqHelp /></Protected>} />
                        </Route>
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </Suspense>
                    <Toaster
                        position="top-right"
                        richColors
                        closeButton
                        duration={5000}
                        visibleToasts={5}
                        gap={10}
                        expand
                    />
                    </ScopeFilterProvider>
                </BrowserRouter>
                </ConfirmProvider>
                </AppBootGate>
                </EntitlementsProvider>
                </SettingsProvider>
            </ThemeProvider>
        </AuthProvider>
    );
}
